/**
 * @file src/shell/DrawCanvas.js
 * Lõi vẽ tự do DÙNG CHUNG giữa PenLayer.js (nét vẽ đè lên sân khấu 3D) và
 * Whiteboard.js (canvas trong panel bảng trắng) — hai canvas HOÀN TOÀN ĐỘC LẬP với
 * nhau, chỉ chung cách dựng: canvas 2D full-bleed trong một khung chứa, DPI-aware,
 * vẽ bằng Pointer Events, và cùng một bảng màu 6 ô + ô "chọn màu khác".
 *
 * Tách ra từ đây vì hai nơi cần Y HỆT logic pointer/resize/clear — trùng đúng một
 * lần là chấp nhận được, trùng hai lần (giờ đã có nơi thứ hai) là tín hiệu nên gom.
 *
 * LƯU TRỮ DẠNG VECTOR, KHÔNG PHẢI BITMAP — mỗi nét vẽ là {color, points}, points là
 * toạ độ TƯƠNG ĐỐI 0..1 theo bề rộng/cao canvas (KHÔNG phải pixel tuyệt đối). Canvas
 * chỉ là nơi HIỂN THỊ, `strokes` (mảng nét) mới là NGUỒN SỰ THẬT. Vì vậy:
 *   · Resize (kéo thanh chia, đổi bề rộng cửa sổ) chỉ cần vẽ lại từ `strokes` theo
 *     kích thước mới — không cần chụp/dán bitmap như bản trước.
 *   · Ẩn/hiện panel (display:none) không còn nguy cơ mất nét: dù canvas.width/height
 *     bị trình duyệt xoá trắng lúc ẩn, `strokes` vẫn còn trong bộ nhớ JS, hiện lại là
 *     vẽ lại đủ y như cũ.
 *   · Undo/Redo chỉ là bớt/thêm phần tử trong `strokes` rồi vẽ lại toàn bộ — không cần
 *     giữ hàng chục bản chụp bitmap nặng nề.
 */

/** 5 màu cơ bản của công cụ chú thích Zoom/PowerPoint, cộng tím — không bịa thêm
 * ngoài quy ước quen thuộc này. Ô "chọn màu khác" (createColorSwatches) cho phép chọn
 * bất kỳ màu nào khác 6 màu này. */
export const DRAW_COLORS = ['#ef4444', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#1c274c'];

/**
 * @typedef {Object} Stroke
 * @property {string} color
 * @property {[number, number][]} points Toạ độ tương đối 0..1.
 */

/**
 * Dựng một canvas 2D full-bleed trong `o.mount`, vẽ bằng Pointer Events, DPI-aware,
 * có undo/redo và xuất PNG. KHÔNG tự quản UI bảng màu — nơi gọi tự quyết, chỉ gọi
 * `setColor()`.
 * @param {Object} o
 * @param {HTMLElement} o.mount
 * @param {string} [o.className]
 * @param {string} [o.initialColor]
 * @param {() => void} [o.onHistoryChange] Gọi sau MỖI lần strokes/undoStack/redoStack
 *   đổi (vẽ xong một nét, undo, redo, xoá) — nơi gọi dùng để cập nhật disabled của nút
 *   Undo/Redo.
 */
export function createDrawCanvas(o) {
  const canvas = document.createElement('canvas');
  if (o.className) canvas.className = o.className;
  o.mount.appendChild(canvas);
  const ctx = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));

  /** @type {string} */
  let color = o.initialColor ?? /** @type {string} */ (DRAW_COLORS[0]);

  /** @type {Stroke[]} Nguồn sự thật — xem chú thích đầu file. */
  let strokes = [];
  /** @type {Stroke | null} Nét đang vẽ, chưa commit vào `strokes`. */
  let current = null;
  /** @type {Array<{type: 'add', stroke: Stroke} | {type: 'clear', removed: Stroke[]}>} */
  const undoStack = [];
  /** @type {typeof undoStack} */
  let redoStack = [];

  /** @param {Stroke} s @param {number} w @param {number} h Vẽ MỘT nét theo kích thước hiện tại. */
  function paintStroke(s, w, h) {
    if (s.points.length === 0) return;
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const p0 = /** @type {[number, number]} */ (s.points[0]);
    if (s.points.length === 1) {
      // Bấm-thả liền một chỗ vẫn phải ra nét — chấm tròn nhỏ thay cho một đường kẻ.
      ctx.beginPath();
      ctx.arc(p0[0] * w, p0[1] * h, 1.5, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(p0[0] * w, p0[1] * h);
    for (let i = 1; i < s.points.length; i++) {
      const p = /** @type {[number, number]} */ (s.points[i]);
      ctx.lineTo(p[0] * w, p[1] * h);
    }
    ctx.stroke();
  }

  /** Vẽ lại TOÀN BỘ từ `strokes` — dùng khi resize/undo/redo/clear, không dùng lúc
   * đang rê chuột (onMove vẽ thêm đoạn mới trực tiếp, xem bên dưới, để không phải vẽ
   * lại từ đầu mỗi frame). */
  function redrawAll() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    ctx.clearRect(0, 0, w, h); // toạ độ CSS — ctx đã setTransform theo dpr ở resize()
    for (const s of strokes) paintStroke(s, w, h);
  }

  /**
   * Đo lại độ nét theo devicePixelRatio bằng `setTransform` (TUYỆT ĐỐI), KHÔNG dùng
   * `scale` (TƯƠNG ĐỐI) — gọi `scale()` hai lần sẽ NHÂN DỒN hệ số. Clamp dpr ở 2 giống
   * Engine.js. w/h = 0 nghĩa là canvas đang display:none (panel Bảng trắng vừa đóng)
   * — bỏ qua, không co canvas về 0: vô hại vì `strokes` không nằm trong canvas.
   */
  function resize() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w <= 0 || h <= 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(w * dpr));
    canvas.height = Math.max(1, Math.round(h * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    redrawAll();
  }
  const ro = new ResizeObserver(resize);
  ro.observe(canvas);
  resize();

  function notifyHistory() {
    o.onHistoryChange?.();
  }

  function clear() {
    if (strokes.length === 0) return;
    undoStack.push({ type: 'clear', removed: strokes.slice() });
    redoStack = [];
    strokes = [];
    redrawAll();
    notifyHistory();
  }

  function undo() {
    const op = undoStack.pop();
    if (!op) return;
    if (op.type === 'add') strokes.pop();
    else strokes = op.removed.slice();
    redoStack.push(op);
    redrawAll();
    notifyHistory();
  }

  function redo() {
    const op = redoStack.pop();
    if (!op) return;
    if (op.type === 'add') strokes.push(op.stroke);
    else strokes = [];
    undoStack.push(op);
    redrawAll();
    notifyHistory();
  }

  /** @param {PointerEvent} e @returns {[number, number]} Toạ độ TƯƠNG ĐỐI 0..1. */
  function pos(e) {
    const r = canvas.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  }

  /** @param {PointerEvent} e */
  const onDown = (e) => {
    canvas.setPointerCapture(e.pointerId);
    current = { color, points: [pos(e)] };
    paintStroke(current, canvas.clientWidth, canvas.clientHeight);
  };
  /** @param {PointerEvent} e */
  const onMove = (e) => {
    if (!current) return;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const prev = /** @type {[number, number]} */ (current.points[current.points.length - 1]);
    const next = pos(e);
    current.points.push(next);
    // Vẽ THÊM đoạn mới trực tiếp — không gọi redrawAll() ở đây, không thì rê chuột dài
    // (hàng trăm điểm) sẽ vẽ lại toàn bộ nét mỗi frame, chậm dần theo độ dài nét.
    ctx.strokeStyle = current.color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(prev[0] * w, prev[1] * h);
    ctx.lineTo(next[0] * w, next[1] * h);
    ctx.stroke();
  };
  const onUp = () => {
    if (!current) return;
    strokes.push(current);
    undoStack.push({ type: 'add', stroke: current });
    redoStack = [];
    current = null;
    notifyHistory();
  };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);

  return {
    canvas,
    /** @param {string} c */
    setColor(c) {
      color = c;
    },
    clear,
    undo,
    redo,
    canUndo: () => undoStack.length > 0,
    canRedo: () => redoStack.length > 0,
    /** Xuất PNG nền trong suốt — chỉ có nét vẽ, không có lưới chấm của panel. */
    toDataURL: () => canvas.toDataURL('image/png'),
    dispose() {
      ro.disconnect();
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.remove();
    },
  };
}

/**
 * Dựng phần DOM của bảng màu: 6 ô cố định (DRAW_COLORS) + một ô "chọn màu khác" bằng
 * <input type="color"> gốc của trình duyệt (chọn được bất kỳ màu nào, không cần tự vẽ
 * color-picker). KHÔNG bọc khung ngoài (sep/nút Xoá) — nơi gọi tự ghép theo bố cục
 * riêng (PenLayer nổi trên sân khấu, Whiteboard nằm trong toolbar của panel).
 * @param {Object} o
 * @param {(color: string) => void} o.onChange
 * @param {string} [o.initial]
 */
export function createColorSwatches(o) {
  const swatchesEl = document.createElement('div');
  swatchesEl.className = 'ol-pen-palette__swatches';

  const customEl = /** @type {HTMLLabelElement} */ (document.createElement('label'));
  customEl.className = 'ol-pen-swatch ol-pen-swatch--custom';
  customEl.innerHTML = `<input type="color" class="ol-pen-swatch__input" />`;
  const customInput = /** @type {HTMLInputElement} */ (
    customEl.querySelector('.ol-pen-swatch__input')
  );

  let color = o.initial ?? /** @type {string} */ (DRAW_COLORS[0]);
  const swatchEls = DRAW_COLORS.map((c) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ol-pen-swatch';
    b.style.background = c;
    b.classList.toggle('is-on', c === color);
    b.addEventListener('click', () => {
      color = c;
      customEl.classList.remove('is-on');
      for (const s of swatchEls) s.classList.toggle('is-on', s === b);
      o.onChange(color);
    });
    swatchesEl.appendChild(b);
    return b;
  });
  // Nền của label theo màu vừa chọn, để ô "chọn màu khác" cũng trông như một swatch
  // bình thường sau khi dùng, thay vì mãi hiện icon bánh xe màu mặc định.
  customInput.addEventListener('input', () => {
    color = customInput.value;
    customEl.style.background = color;
    customEl.classList.add('is-on');
    for (const s of swatchEls) s.classList.remove('is-on');
    o.onChange(color);
  });

  return { swatchesEl, customEl };
}
